import { PageProps } from './client';

// Structural: a TanStack Router without importing TanStack, so this stays
// dependency-free. Called once where an app creates its router. Driven by the
// committed route matches, with onResolved as a fallback for screens that
// emit no events. Location can move ahead of matches during navigation.
export interface PageViewRouter {
  state: {
    matches: ReadonlyArray<{ routeId: string; pathname?: string }>;
    location: { href: string };
  };
  subscribe: (
    event: 'onResolved',
    cb: (e: { toLocation: { href: string } }) => void
  ) => () => void;
}

export interface PageViewSink {
  page: (props: PageProps) => void;
  setPageContextResolver?: (
    resolver: () => PageProps | undefined
  ) => () => void;
  syncPageContext?: () => void;
}

export const subscribeRouterPageViews = (
  router: PageViewRouter,
  sink: PageViewSink
) => {
  // Match pathnames belong to the committed matches. location.href can
  // already point at the destination while the previous screen is mounted.
  const clearResolver = sink.setPageContextResolver?.(() => {
    const match = router.state.matches.at(-1);
    return match?.pathname
      ? { route: match.routeId, path: match.pathname }
      : undefined;
  });
  let last: string | undefined;
  const track = (href: string) => {
    if (
      clearResolver &&
      sink.syncPageContext &&
      router.state.matches.at(-1)?.pathname
    ) {
      sink.syncPageContext();
      // Preserve existing query-only navigation page views. A route change
      // already has its context established by syncPageContext above.
      if (last && href !== last && href.split('?')[0] === last.split('?')[0]) {
        const match = router.state.matches.at(-1);
        sink.page({
          route: match?.routeId,
          path: match?.pathname ?? href.split('?')[0]
        });
      }
      last = href;
      return;
    }
    if (href === last) return;
    last = href;
    const matches = router.state.matches;
    const route = matches[matches.length - 1]?.routeId;
    sink.page({ route, path: href.split('?')[0] });
  };
  const unsubscribe = router.subscribe('onResolved', ({ toLocation }) =>
    track(toLocation.href)
  );
  return () => {
    unsubscribe();
    clearResolver?.();
  };
};
